import type { DataProvider, Identifier } from "ra-core";

import { dialCodeForIso } from "./dialCodes";

const MIN_PHONE_DIGITS = 7;
const PAGE_SIZE = 1000;

export interface DuplicatePhone {
  number?: string | null;
  dial_code?: string | null;
  dial_iso?: string | null;
}

export interface DuplicateContactCandidate {
  id?: Identifier | null;
  first_name?: string | null;
  last_name?: string | null;
  phone_jsonb?: DuplicatePhone[] | null;
}

export interface DuplicateCompanyCandidate {
  id?: Identifier | null;
  name?: string | null;
}

export class DuplicateRecordError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DuplicateRecordError";
  }
}

export function normalizePersonName(value: string | null | undefined): string {
  return (value ?? "").trim().replace(/\s+/g, " ").toLocaleLowerCase("es-CO");
}

export function normalizePhoneDigits(value: string | null | undefined): string {
  return (value ?? "").replace(/\D/g, "");
}

export function collectPhoneDigits(
  phones: DuplicatePhone[] | null | undefined,
): string[] {
  const digits = (phones ?? [])
    .map((phone) => {
      const local = normalizePhoneDigits(phone.number);
      const dial =
        normalizePhoneDigits(phone.dial_code) || dialCodeForIso(phone.dial_iso);
      if (local.length === 0) {
        return "";
      }
      if (dial.length > 0 && !local.startsWith(dial)) {
        return `${dial}${local}`;
      }
      return local;
    })
    .filter((phone) => phone.length >= MIN_PHONE_DIGITS);
  return [...new Set(digits)];
}

function isSameRecord(
  left: Identifier | null | undefined,
  right: Identifier | null | undefined,
): boolean {
  if (left == null || right == null) {
    return false;
  }
  return String(left) === String(right);
}

export function findDuplicateContact(
  candidate: DuplicateContactCandidate,
  existing: DuplicateContactCandidate[],
): DuplicateContactCandidate | null {
  const phones = collectPhoneDigits(candidate.phone_jsonb);
  const firstName = normalizePersonName(candidate.first_name);
  const lastName = normalizePersonName(candidate.last_name);
  const hasFullName = firstName.length > 0 && lastName.length > 0;

  for (const row of existing) {
    if (isSameRecord(candidate.id, row.id)) {
      continue;
    }
    const rowPhones = collectPhoneDigits(row.phone_jsonb);
    if (phones.some((phone) => rowPhones.includes(phone))) {
      return row;
    }
    if (
      hasFullName &&
      normalizePersonName(row.first_name) === firstName &&
      normalizePersonName(row.last_name) === lastName
    ) {
      return row;
    }
  }

  return null;
}

export function findDuplicateCompany(
  candidate: DuplicateCompanyCandidate,
  existing: DuplicateCompanyCandidate[],
): DuplicateCompanyCandidate | null {
  const name = normalizePersonName(candidate.name);
  if (name.length === 0) {
    return null;
  }
  for (const row of existing) {
    if (isSameRecord(candidate.id, row.id)) {
      continue;
    }
    if (normalizePersonName(row.name) === name) {
      return row;
    }
  }
  return null;
}

export function duplicateContactMessage(
  match: DuplicateContactCandidate,
): string {
  const name = [match.first_name, match.last_name]
    .map((part) => (part ?? "").trim())
    .filter((part) => part.length > 0)
    .join(" ");
  if (name.length > 0) {
    return `Ya está creado: ${name}.`;
  }
  return "Ya está creado.";
}

export function duplicateCompanyMessage(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length > 0) {
    return `Ya está creada: ${trimmed}.`;
  }
  return "Ya está creada.";
}

async function listRecords<T extends { id: Identifier }>(
  dataProvider: DataProvider,
  resource: string,
): Promise<T[]> {
  const rows: T[] = [];
  let page = 1;
  let total = PAGE_SIZE;

  while (rows.length < total && page <= 10) {
    const result = await dataProvider.getList<T>(resource, {
      pagination: { page, perPage: PAGE_SIZE },
      sort: { field: "id", order: "ASC" },
      filter: {},
    });
    rows.push(...result.data);
    total = result.total ?? rows.length;
    if (result.data.length < PAGE_SIZE) {
      break;
    }
    page += 1;
  }

  return rows;
}

export async function assertContactIsUnique(
  dataProvider: DataProvider,
  candidate: DuplicateContactCandidate,
): Promise<void> {
  const existing = await listRecords<DuplicateContactCandidate & { id: Identifier }>(
    dataProvider,
    "contacts",
  );
  const match = findDuplicateContact(candidate, existing);
  if (match) {
    throw new DuplicateRecordError(duplicateContactMessage(match));
  }
}

export async function findExistingCompany(
  dataProvider: DataProvider,
  candidate: DuplicateCompanyCandidate,
): Promise<DuplicateCompanyCandidate | null> {
  const existing = await listRecords<DuplicateCompanyCandidate & { id: Identifier }>(
    dataProvider,
    "companies",
  );
  return findDuplicateCompany(candidate, existing);
}

export async function assertCompanyIsUnique(
  dataProvider: DataProvider,
  candidate: DuplicateCompanyCandidate,
): Promise<void> {
  const match = await findExistingCompany(dataProvider, candidate);
  if (match) {
    throw new DuplicateRecordError(
      duplicateCompanyMessage(match.name ?? candidate.name ?? ""),
    );
  }
}
